import { useState } from 'react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';

import Swal from 'sweetalert2';
import { Trash } from 'lucide-react';
import { urls } from '../../services/apiService';

export default function SearchDevicesDialog({ isOpen, onClose }) {
  const [email, setEmail] = useState('');
  const [devices, setDevices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSearch = async () => {
    if (!email) {
      setError('Email is required');
      return;
    }

    setIsLoading(true);
    setError(null);
    setDevices([]);

    try {
      const response = await fetch(urls.search_devices, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to search devices');
      }

      setDevices(data.devices || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteDevice = async (deviceId) => {
    try {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: `You want to remove device ${deviceId}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!'
      });

      if (result.isConfirmed) {
        const response = await fetch(urls.delete_device, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            device_id: deviceId,
            email: email}),
        });

        if (!response.ok) {
          throw new Error('Failed to delete device');
        }

        // Remove the deleted device from the local state
        setDevices(devices.filter(device => device !== deviceId));
        
        Swal.fire(
          'Deleted!',
          `Device ${deviceId} has been removed.`,
          'success'
        );
      }
    } catch (err) {
      Swal.fire(
        'Error!',
        err.message || 'Failed to delete device',
        'error'
      );
    }
  };

  const handleClose = () => {
    setEmail('');
    setDevices([]);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30">
      <DialogPanel className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full mx-4">
        <DialogTitle className="flex justify-between text-2xl font-bold mb-6 text-gray-800">
          Search Assigned Devices
          <button
            onClick={handleClose}
            className="px-2 rounded-lg text-gray-400 hover:text-red-400 transition-colors"
          >
            ×
          </button>
        </DialogTitle>
        
        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label htmlFor="searchEmail" className="block text-sm font-medium text-gray-700 mb-1">
              User Email
            </label>
            <div className="flex gap-2">
              <input
                id="searchEmail"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="user@example.com"
              />
              <button
                onClick={handleSearch}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:bg-blue-400"
                disabled={isLoading}
              >
                {isLoading ? 'Searching...' : 'Search'}
              </button>
            </div>
          </div>

          {devices.length > 0 && (
            <div className="mt-4">
              <h3 className="font-medium text-gray-700 mb-2">Assigned Devices:</h3>
              <ul className="border border-gray-200 rounded-lg divide-y divide-gray-200">
                {devices.map((device, index) => (
                  <li key={index} className="p-3 hover:bg-gray-50 flex justify-between items-center">
                    <span>{device}</span>
                    <button
                      onClick={() => handleDeleteDevice(device)}
                      className="text-red-500 hover:text-red-700 transition-colors"
                      title="Remove device"
                    >
                      <Trash className="h-5 w-5" />
                    
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {devices.length === 0 && !isLoading && email && (
            <div className="p-4 text-center text-gray-500">
              No devices found for this email
            </div>
          )}
        </div>
      </DialogPanel>
    </Dialog>
  );
}